import {
    QueryClient,
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { ErrorType, GUID, InternalUserProfile, PersonOrg } from '@ndvr/auth-api';
import {
    CommandCenterPersonProfile,
    DataType,
    GlobalAccount,
    PersonOrgProfile,
    PersonOrgType,
    UnencryptedData,
} from '@ndvr/customer-api';

import { forgetRecentSearchEntity } from '@/components/recent-search-store';
import { AuthApi, CustomerApi } from 'API';
import { logger } from 'utils';
import { Toast } from 'widgets';

import { useAuthContext } from '../auth/Auth';
import { useSelectedTenantGUIDs } from '../auth/tenant-selection';
import { OrgFormValues, PersonFormValues } from './person-form-model';
import { createOrUpdateOrg, createOrUpdatePerson, SaveOrgGUIDsType, SavePersonContext } from './person-form-utils';
import { SuggestedPersonQueryKeys } from './suggested-person-queries';

export type PersonCoreData = {
    personOrgGUID: GUID<PersonOrg>;
    personOrgType?: PersonOrgType;
    firstName?: string;
    lastName?: string;
    orgName?: string;
    email?: string;
};

const commandCenterPersonProfileToCoreData = (profile: CommandCenterPersonProfile): PersonCoreData => ({
    personOrgGUID: profile.personOrgShortProfile.personOrgGUID,
    personOrgType: profile.personOrgShortProfile.personOrgType,
    firstName: profile.personOrgShortProfile.firstName?.plainText || profile.personOrgShortProfile.firstNamePlain,
    lastName: profile.personOrgShortProfile.lastName?.plainText || profile.personOrgShortProfile.lastNamePlain,
    orgName: profile.personOrgShortProfile.orgName?.plainText || profile.personOrgShortProfile.orgNamePlain,
    email:
        profile.personOrgShortProfile.primaryEmail?.plainText || profile.personOrgShortProfile.primaryEmailPlain || '',
});

export const usePersonProfilesForTenant = () => {
    const { currentTenant } = useAuthContext();
    return useQuery({
        enabled: !!currentTenant?.tenantGUID,
        queryKey: ['tenant', currentTenant?.tenantGUID, 'personOrg', 'list'],
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterPersonProfilesForTenant(currentTenant.tenantGUID);
            return response ?? undefined;
        },
    });
};

export const usePersonCoreDataForTenant = (tenantGUID?: string) =>
    useQuery({
        enabled: !!tenantGUID,
        queryKey: ['tenant', tenantGUID, 'personOrg', 'core-list'],
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterPersonProfilesForTenant(tenantGUID!);
            return response?.map(commandCenterPersonProfileToCoreData) ?? [];
        },
    });

export const usePersonProfilesForTenants = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        enabled: tenantGUIDs.length > 0,
        queryKey: ['tenants', tenantGUIDs, 'personOrg', 'list'],
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterPersonProfilesForTenants(tenantGUIDs);
            return response ?? undefined;
        },
    });
};

export const useLoginAsCommandCenterUser = () =>
    useMutation({
        mutationFn: async (personOrgGUID: GUID<PersonOrg>) => {
            const [response] = await AuthApi().loginAsCommandCenterUser(personOrgGUID);
            const loginToken = response?.token;
            if (!response || !loginToken) {
                throw new Error('Failed to get login token');
            }

            logger.info('Opening Command Center as impersonated user');

            // open Command Center login in new tab with impersonation flag
            const ccBaseUrl = window.location.origin;
            window.open(`${ccBaseUrl}/login/${loginToken}?impersonation=true`, '_blank');
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to login as user');
        },
    });

type InternalUserCoreIdentity = Pick<InternalUserProfile, 'profileGUID' | 'firstName' | 'lastName' | 'email'>;

// Backend bridge: InternalUserProfile.profileGUID and its PersonOrg GUID share
// the same UUID — see InternalUserProfile#getAsPersonOrgGUID in the auth service.
const asPersonOrgGUID = (profileGUID: GUID<InternalUserProfile>): GUID<PersonOrg> =>
    profileGUID as unknown as GUID<PersonOrg>;

export const internalUserToCoreData = (user: InternalUserCoreIdentity): PersonCoreData => ({
    personOrgGUID: asPersonOrgGUID(user.profileGUID),
    personOrgType: PersonOrgType.PERSON,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
});

export const usePersonOrg = (
    personOrgGUID?: GUID<PersonOrg>,
    options?: Partial<UseQueryOptions<PersonOrg | undefined>>,
) =>
    useQuery({
        ...options,
        queryKey: ['personOrg', personOrgGUID],
        queryFn: async () => {
            const [response] = await CustomerApi().getPersonOrg(personOrgGUID!);
            return response ?? undefined;
        },
        enabled: !!personOrgGUID,
    });

export const usePersonOrgProfile = (personOrgGUID?: GUID<PersonOrg>) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'profile'],
        queryFn: async () => {
            const [response] = await CustomerApi().getPersonOrgProfile(personOrgGUID!);
            return response ?? undefined;
        },
        enabled: !!personOrgGUID,
    });

export const useOrgLinks = (personOrgGUID?: GUID<PersonOrg>) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'links'],
        queryFn: async () => {
            const [response] = await CustomerApi().getPersonOrgLinksForParent(personOrgGUID!);
            return response ?? undefined;
        },
        enabled: !!personOrgGUID,
    });

export const usePersonOrgTaxId = (personOrgGUID: string) =>
    useQuery<UnencryptedData | null>({
        queryKey: ['personOrg', personOrgGUID, 'taxId'],
        queryFn: async () => {
            const [response] = await CustomerApi({ hideError: true }).getIdentifyingTaxId(personOrgGUID);
            return response ?? null;
        },
        enabled: false,
        gcTime: 0,
        retry: false,
    });

export const useSetPersonOrgTaxId = (personOrgGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (plainText: string) =>
            CustomerApi().setIdentifyingTaxId(personOrgGUID, {
                encryptionContextGUID: personOrgGUID,
                dataType: DataType.PERSON_ORG_INFORMATION_IDENTIFYING_TAX_ID,
                plainText,
            }),
        onSuccess: () => {
            Toast.success('Tax ID updated');
            queryClient.invalidateQueries({ queryKey: ['personOrg', personOrgGUID] });
            queryClient.removeQueries({ queryKey: ['personOrg', personOrgGUID, 'taxId'] });
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to update Tax ID');
        },
    });
};

export const usePersonOrgFinancialProfile = (personOrgGUID: string) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'financialProfile'],
        queryFn: async () => {
            const [response] = await CustomerApi().getPersonOrgFinancialProfile(personOrgGUID);
            return response ?? undefined;
        },
    });

export const usePersonOrgProfilesForHousehold = (
    globalAccountGUID: string,
    options?: Partial<UseQueryOptions<PersonOrgProfile[] | undefined>>,
) =>
    useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'personOrgProfiles'],
        queryFn: async () => {
            const [personOrgProfiles] = await CustomerApi().getPersonOrgProfilesForHousehold(globalAccountGUID);
            return personOrgProfiles ?? undefined;
        },
        ...options,
    });

export const useClientPersonOrgs = () =>
    useQuery({
        queryKey: ['personOrg', 'clientPersonOrgs'],
        queryFn: async () => {
            const [response] = await CustomerApi().getClientPersonOrgs();
            return response ?? undefined;
        },
    });

export const useClientPersonOrgMap = () => {
    const { data } = useClientPersonOrgs();
    return useMemo(
        () =>
            data?.reduce(
                (map, personOrg) => map.set(personOrg.personOrgGUID, personOrg),
                new Map<string, PersonOrg>(),
            ) || new Map<string, PersonOrg>(),
        [data],
    );
};

export const useInvitePerson = (tenantGUID?: string) => {
    const queryClient = useQueryClient();
    const { currentTenant } = useAuthContext();
    return useMutation({
        mutationFn: (personOrgGUID: GUID<PersonOrg>) => {
            return AuthApi({ hideError: true }).sendInviteForPersonOrg(
                tenantGUID ?? currentTenant?.tenantGUID,
                personOrgGUID,
            );
        },
        onSuccess: (_, personOrgGUID) => {
            Toast.success('Person invite sent');
            queryClient.invalidateQueries({ queryKey: ['onboarding'] });
            queryClient.invalidateQueries({
                queryKey: ['personOrg', personOrgGUID, 'inviteDetails'],
            });
        },
        onError: (error: any) => {
            if (error.errorType === ErrorType.MISSING_PRIMARY_EMAIL) {
                Toast.error('Add an email address before inviting this person', 'info');
            } else {
                Toast.forError(error, 'Failed to send invite');
            }
        },
    });
};

export const useInviteDetails = (personOrgGUID?: GUID<PersonOrg>) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'inviteDetails'],
        queryFn: async () => {
            const [response] = await AuthApi().getInviteDetails(personOrgGUID!);
            return response;
        },
        enabled: !!personOrgGUID,
    });

type SavePersonProfileInput = SavePersonContext | { personGUID: GUID<PersonOrg> };

export const useSavePersonProfile = (
    input: SavePersonProfileInput,
    options?: { invalidateGlobalAccountGUID?: GUID<GlobalAccount> },
) => {
    const context: SavePersonContext = 'mode' in input ? input : { mode: 'edit', personGUID: input.personGUID };
    const personGUID = context.mode === 'edit' ? context.personGUID : undefined;
    const globalAccountGUID =
        options?.invalidateGlobalAccountGUID ??
        (context.mode === 'create-household' ? context.globalAccountGUID : undefined);
    const { currentTenant } = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: PersonFormValues) => createOrUpdatePerson(context, data),
        onSuccess: () => {
            Toast.success(`Profile ${personGUID ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['personOrg', 'list'],
            });
            queryClient.invalidateQueries({
                queryKey: ['tenant', currentTenant.tenantGUID, 'personOrg', 'list'],
            });
            queryClient.invalidateQueries({
                queryKey: ['tenants'],
                predicate: ({ queryKey }) => queryKey[2] === 'personOrg' && queryKey[3] === 'list',
            });
            if (personGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['personOrg', personGUID],
                });
            }
            if (globalAccountGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['globalAccount', globalAccountGUID],
                });
            }
            if (context.mode === 'create-household' && context.link?.noteGUID) {
                queryClient.invalidateQueries({
                    queryKey: SuggestedPersonQueryKeys.byNote(context.link.noteGUID),
                });
            }
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to save profile');
        },
    });
};

export const useSaveOrgProfile = (props: SaveOrgGUIDsType) => {
    const { orgGUID, globalAccountGUID } = props;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: OrgFormValues) => createOrUpdateOrg(props, data),
        onSuccess: () => {
            Toast.success(`Profile ${orgGUID ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['personOrg', 'list'],
            });
            if (orgGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['personOrg', orgGUID],
                });
            }
            if (globalAccountGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['globalAccount', globalAccountGUID],
                });
            }
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to save profile');
        },
    });
};

export const useAdminPasswordReset = (personOrgGUID: string) =>
    useMutation({
        mutationFn: () => {
            return AuthApi({ hideError: true }).adminPasswordReset(personOrgGUID);
        },
        onSuccess: () => {
            Toast.success('Password reset email sent');
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to send reset link');
        },
    });

export const useToggleUserCredential = (personOrgGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (shouldDisable: boolean) => {
            if (shouldDisable) {
                const disableRequest = { disabledReason: 'Disabled from CommandCenter' };
                return AuthApi().disableCredential(personOrgGUID, disableRequest);
            }

            return AuthApi().enableCredential(personOrgGUID);
        },
        onSuccess: (_, shouldDisable) => {
            Toast.success(`User credentials ${shouldDisable ? 'disabled' : 'enabled'}`);
            queryClient.invalidateQueries({
                queryKey: ['personOrg', personOrgGUID],
            });
        },
        onError: (error, shouldDisable) => {
            Toast.forError(error, `Failed to ${shouldDisable ? 'disable' : 'enable'} user credentials`);
        },
    });
};

export const usePersonCredential = (personOrgGUID?: string) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'credential'],
        queryFn: async () => {
            const [response] = await AuthApi().getPersonCredential(personOrgGUID!);
            return response;
        },
        enabled: !!personOrgGUID,
    });

/**
 * Drops every cached query about one person, so nothing refetches a deleted person and fails. A mounted view that still
 * observes a dropped query refetches it on its next render, so call this only once those views unmount.
 */
export const removePersonQueries = (queryClient: QueryClient, personOrgGUID: string) => {
    queryClient.removeQueries({ queryKey: ['personOrg', personOrgGUID] });
    queryClient.removeQueries({ queryKey: ['person', personOrgGUID] });
};

export const useCognitoDetails = (personOrgGUID: string) =>
    useQuery({
        queryKey: ['personOrg', personOrgGUID, 'cognitoDetails'],
        queryFn: async () => {
            const [response] = await AuthApi().getCognitoUserDetails(personOrgGUID);
            return response;
        },
        throwOnError: false,
    });

export const useDeletePersonOrg = (personOrgGUID: string, mutationOptions?: UseMutationOptions) => {
    const queryClient = useQueryClient();

    return useMutation({
        ...mutationOptions,
        mutationFn: async () => {
            const [response] = await CustomerApi({ hideError: true }).deletePersonOrg(personOrgGUID);
            return response ?? undefined;
        },
        onSuccess: (data, variables, onMutateResult, context) => {
            if (data?.valid === true) {
                forgetRecentSearchEntity(personOrgGUID);
            }
            Toast.success(`Person deleted ${personOrgGUID}`);
            queryClient.invalidateQueries({
                queryKey: ['personOrg', 'list'],
            });
            mutationOptions?.onSuccess?.(data, variables, onMutateResult, context);
        },
        onError: (e: any, variables, onMutateResult, context) => {
            Toast.forError(e, `Failed to delete person ${personOrgGUID}`);
            mutationOptions?.onError?.(e, variables, onMutateResult, context);
        },
    });
};
